import { translate } from '@teamleader/i18n';
import { IconChevronDownSmallOutline, IconChevronRightSmallOutline } from '@teamleader/ui-icons';
import cx from 'classnames';
import React, { useCallback, useContext } from 'react';

import Invoice from './Invoice';
import Box from '../../../../Components/UI/Box';
import Counter from '../../../../Components/UI/Counter';
import Spinner from '../../../../Components/UI/Spinner';
import { ListInvoice } from '../../../../Types/invoice';
import { EVENTS, track } from '../../../../Utils/analytics';
import { themeContext } from '../../ThemeLoader';
import { MAX_RELIABLE_INVOICE_COUNT, useInvoicesListResult } from '../../Types/Invoices/useInvoicesList';
import styles from '../styles.css';

interface InvoiceListProps {
  title: string;
  invoices?: useInvoicesListResult;
  isCollapsed: boolean;
  onInvoiceClick?: (invoice: ListInvoice) => void;
  handleCollapse: (state: boolean) => void;
}

const InvoiceList: React.FC<InvoiceListProps> = ({ title, isCollapsed, handleCollapse, invoices, onInvoiceClick }) => {
  const { theme } = useContext(themeContext);

  const handleToggleCollapse = useCallback(() => {
    handleCollapse(!isCollapsed);
  }, [handleCollapse, isCollapsed]);

  const handleLoadMore = useCallback(() => {
    invoices!.loadMore();
    track(EVENTS.CLICKED_LOAD_MORE);
  }, [invoices]);

  if (!invoices || invoices.data?.length === 0) {
    return null;
  }

  const Icon = isCollapsed ? IconChevronRightSmallOutline : IconChevronDownSmallOutline;
  return (
    <>
      <Box
        element="button"
        className={styles['item']}
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        onClick={handleToggleCollapse}
      >
        <Icon className={styles['icon']} />
        <Box flex={1} marginLeft={3} display="flex" justifyContent="space-between" alignItems="center">
          {title}
          <Counter
            theme={`menu-${theme}`}
            count={
              invoices.totalAmount < MAX_RELIABLE_INVOICE_COUNT
                ? invoices.totalAmount
                : `${MAX_RELIABLE_INVOICE_COUNT}+`
            }
          />
        </Box>
      </Box>
      {!isCollapsed && (
        <Box className={styles['sub-items']}>
          {invoices.data?.map((invoice) => (
            <Invoice key={invoice.id} invoice={invoice} onClick={onInvoiceClick} />
          ))}
          {invoices.hasMore && (
            <Box
              element="button"
              onClick={handleLoadMore}
              className={cx(styles['sub-item'], styles['load-more-button'])}
              disabled={invoices.loading}
            >
              {invoices.loading ? (
                <Spinner inverse className={styles['load-more-button-icon']} />
              ) : (
                <>
                  {translate('actions.loadMore')}
                  <IconChevronDownSmallOutline className={styles['load-more-button-icon']} />
                </>
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default InvoiceList;
